import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card, CardBody, Col, Container, Row, Nav, NavItem, NavLink,
  TabContent, TabPane, Badge, Button, Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
  Spinner
} from 'reactstrap';
import classnames from "classnames";
import { toast } from 'react-toastify';

// Import landing page components
import Navbar from './Navbar';
import Footer from './Footer';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';

// Import auth context
import { useAuth } from '../../../contexts/AuthContext';
import { api } from '../../../services/api';

// Large scale plans - separate cards
const LARGE_SCALE_PLANS = [
  { plan: "enterprise", name: "Enterprise", products: 5000, monthlyPrice: 99.90, yearlyPrice: 959, icon: "ri-building-4-line", color: "warning", syncInterval: "10 min", ebayAccounts: 3 },
  { plan: "enterprise_plus", name: "Enterprise Plus", products: 10000, monthlyPrice: 149.90, yearlyPrice: 1439, icon: "ri-building-4-fill", color: "warning", syncInterval: "5 min", ebayAccounts: 5 },
  { plan: "agency", name: "Agency", products: 25000, monthlyPrice: 249.90, yearlyPrice: 2399, icon: "ri-team-line", color: "info", syncInterval: "5 min", ebayAccounts: 5 },
];

// Ultimate tier options (dropdown)
const ULTIMATE_TIERS = [
  { products: 50000, monthlyPrice: 399.90, yearlyPrice: 3839, plan: "ultimate", label: "50,000 SKUs" },
  { products: 75000, monthlyPrice: 549.90, yearlyPrice: 5279, plan: "ultimate_plus", label: "75,000 SKUs" },
  { products: 100000, monthlyPrice: 699.90, yearlyPrice: 6719, plan: "titan", label: "100,000 SKUs (Titan)" },
];

// Package features
const FEATURES = {
  starter: [
    { text: "AI-Powered Category Matching", included: true, ai: true },
    { text: "Amazon to eBay Listing", included: true },
    { text: "Price & Stock Sync (2h)", included: true },
    { text: "1 eBay Account", included: true },
    { text: "3 HTML Templates", included: true },
    { text: "Basic Dashboard", included: true },
    { text: "Automatic Ordering (AO)", included: true },
    { text: "Auto Buyer Messages", included: true },
    { text: "Daily Email Reports", included: true },
    { text: "Safe Repricing Protection", included: true },
    { text: "Gmail Integration", included: true },
    { text: "8 eBay + 8 Amazon Marketplaces", included: true },
    { text: "Email Support", included: true },
  ],
  advanced: [
    { text: "AI-Powered Category Matching", included: true, ai: true },
    { text: "AI Title Optimization", included: true, ai: true },
    { text: "Amazon to eBay Listing", included: true },
    { text: "Price & Stock Sync (1h)", included: true },
    { text: "2 eBay Accounts", included: true },
    { text: "5 HTML Templates", included: true },
    { text: "Full Dashboard", included: true },
    { text: "Automatic Ordering (AO)", included: true },
    { text: "Auto Buyer Messages", included: true },
    { text: "Daily Email Reports", included: true },
    { text: "Safe Repricing Protection", included: true },
    { text: "Gmail Integration", included: true },
    { text: "8 eBay + 8 Amazon Marketplaces", included: true },
    { text: "Email Support", included: true },
  ],
  professional: [
    { text: "AI-Powered Category Matching", included: true, ai: true },
    { text: "AI Title & Description", included: true, ai: true },
    { text: "AI Smart Repricing", included: true, ai: true },
    { text: "Amazon to eBay Listing", included: true },
    { text: "Price & Stock Sync (30min)", included: true },
    { text: "2 eBay Accounts", included: true },
    { text: "7 HTML Templates", included: true },
    { text: "Full Dashboard + Reports", included: true },
    { text: "Automatic Ordering (AO)", included: true },
    { text: "Auto Buyer Messages", included: true },
    { text: "Daily Email Reports", included: true },
    { text: "Safe Repricing Protection", included: true },
    { text: "Gmail Integration", included: true },
    { text: "8 eBay + 8 Amazon Marketplaces", included: true },
    { text: "Priority Support", included: true },
  ],
  business: [
    { text: "AI-Powered Category Matching", included: true, ai: true },
    { text: "AI Title & Description", included: true, ai: true },
    { text: "AI Smart Repricing", included: true, ai: true },
    { text: "Amazon to eBay Listing", included: true },
    { text: "Price & Stock Sync (15min)", included: true },
    { text: "3 eBay Accounts", included: true },
    { text: "All HTML Templates", included: true },
    { text: "Full Dashboard + Reports", included: true },
    { text: "Bulk Import/Export", included: true },
    { text: "Automatic Ordering (AO)", included: true },
    { text: "Auto Buyer Messages", included: true },
    { text: "Daily Email Reports", included: true },
    { text: "Safe Repricing Protection", included: true },
    { text: "Gmail Integration", included: true },
    { text: "8 eBay + 8 Amazon Marketplaces", included: true },
    { text: "Priority Support", included: true },
  ],
};

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('1');
  const [ultimateDropdownOpen, setUltimateDropdownOpen] = useState(false);
  const [selectedUltimateTier, setSelectedUltimateTier] = useState(0);
  const [loading, setLoading] = useState(false);

  // Check if user came from setup flow
  const isInSetupFlow = isAuthenticated && user && !user.setup_completed;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Complete setup with the given plan
  const completeSetupWithPlan = async (planName: string) => {
    try {
      setLoading(true);

      // Get selected channel from localStorage
      const selectedChannel = localStorage.getItem('selected_channel') || 'ebay';

      // Build request body
      const requestBody: any = {
        plan: planName,
        channel: selectedChannel,
      };

      // Complete setup with plan info
      const response = await api.post<any>('/api/auth/complete-setup', requestBody);

      if (response.success) {
        // Update user in context with new plan info
        updateUser({
          setup_completed: true,
          subscription_plan: planName,
          max_products: response.max_products,
        });

        // Clear the pre-selected plan from localStorage
        localStorage.removeItem('preselected_plan');

        toast.success('Setup completed! Now connect your eBay account.');

        // Redirect to eBay connect (since eBay is currently the only active channel)
        navigate('/ebay-connect');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  // Check for pre-selected plan when in setup flow
  useEffect(() => {
    if (isInSetupFlow) {
      const preselectedPlan = localStorage.getItem('preselected_plan');
      if (preselectedPlan) {
        // Auto-complete setup with the pre-selected plan
        completeSetupWithPlan(preselectedPlan);
      }
    }
  }, [isInSetupFlow]);

  // Handle plan selection
  const handleSelectPlan = async (planName: string) => {
    if (!isInSetupFlow) {
      // Not in setup flow - save plan and go to register
      localStorage.setItem('preselected_plan', planName);
      navigate('/register');
      return;
    }

    // In setup flow - complete setup directly
    await completeSetupWithPlan(planName);
  };

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const getYearlyPrice = (monthlyPrice: number) => {
    return Math.round(monthlyPrice * 12 * 0.75);
  };

  return (
    <div className="layout-wrapper landing">
      {/* Navigation */}
      <SeoHead
        title="Pricing — CodeRom Dropshipping Automation"
        description="Simple, transparent CodeRom pricing. Compare plans for AI listing, repricing & stock sync. Free trial."
      />
      <Navbar />

      {/* Pricing Content */}
      <div style={{ paddingTop: '80px', background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)', minHeight: '100vh' }}>
        <Container>

          {/* Setup Flow Banner */}
          {isInSetupFlow && (
            <div className="text-center pt-4 pb-2">
              <div className="d-inline-flex align-items-center px-4 py-2 rounded-pill" style={{
                background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
                border: '1px solid #93c5fd'
              }}>
                <i className="ri-sparkling-fill me-2" style={{ color: '#3b82f6' }}></i>
                <span className="fw-semibold" style={{ color: '#1e40af' }}>
                  Step 2 of 4: Choose Your Plan
                </span>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="text-center py-5">
            <h1 className="display-5 fw-bold mb-3" style={{ color: '#1e293b' }}>
              Simple, Transparent Pricing
            </h1>
            <p className="lead text-muted mb-4">
              Start free, upgrade when you're ready. No hidden fees.
            </p>

            {/* Billing Toggle */}
            <Nav className="nav-pills plan-nav rounded-pill p-1 d-inline-flex" style={{ background: '#f1f5f9' }}>
              <NavItem>
                <NavLink
                  href="#"
                  className={classnames({ active: activeTab === '1' }, 'rounded-pill px-4 py-2')}
                  onClick={(e) => { e.preventDefault(); toggleTab('1'); }}
                  style={{
                    color: activeTab === '1' ? '#fff' : '#64748b',
                    background: activeTab === '1' ? '#f97316' : 'transparent',
                    fontWeight: '600'
                  }}
                >
                  Monthly
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  href="#"
                  className={classnames({ active: activeTab === '2' }, 'rounded-pill px-4 py-2')}
                  onClick={(e) => { e.preventDefault(); toggleTab('2'); }}
                  style={{
                    color: activeTab === '2' ? '#fff' : '#64748b',
                    background: activeTab === '2' ? '#f97316' : 'transparent',
                    fontWeight: '600'
                  }}
                >
                  Yearly <Badge color="success" className="ms-1">-25%</Badge>
                </NavLink>
              </NavItem>
            </Nav>
          </div>

          {/* Main Plans - Row 1: Starter, Advanced, Professional, Business */}
          <Row className="g-4 justify-content-center mb-4">
            {/* Starter */}
            <Col xl={3} lg={4} md={6}>
              <Card className="pricing-card h-100 border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                <CardBody className="p-4 d-flex flex-column">
                  <div className="d-flex align-items-center mb-4">
                    <div className="flex-shrink-0">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{
                        width: '48px', height: '48px',
                        background: 'linear-gradient(135deg, #e0f2fe, #bae6fd)'
                      }}>
                        <i className="ri-rocket-line fs-20" style={{ color: '#0284c7' }}></i>
                      </div>
                    </div>
                    <div className="ms-3">
                      <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Starter</h5>
                      <span className="text-muted small">Up to 200 SKUs</span>
                    </div>
                  </div>

                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      <div className="mb-4">
                        <span className="fs-14 text-muted">$</span>
                        <span className="display-5 fw-bold" style={{ color: '#1e293b' }}>14.90</span>
                        <span className="text-muted">/month</span>
                      </div>
                    </TabPane>
                    <TabPane tabId="2">
                      <div className="mb-4">
                        <span className="fs-14 text-muted">$</span>
                        <span className="display-5 fw-bold" style={{ color: '#1e293b' }}>143</span>
                        <span className="text-muted">/year</span>
                        <div><small className="text-success">Save $36/year</small></div>
                      </div>
                    </TabPane>
                  </TabContent>

                  <p className="text-muted small mb-4">Perfect for getting started with automated listing</p>

                  <ul className="list-unstyled mb-4 flex-grow-1">
                    {FEATURES.starter.map((feature, idx) => (
                      <li key={idx} className="d-flex align-items-start mb-2">
                        <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                        <span className="text-muted small">
                          {feature.ai && <Badge color="info" className="me-1" pill style={{fontSize: '9px'}}>AI</Badge>}
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="w-100 mt-auto"
                    style={{
                      background: 'transparent',
                      border: '2px solid #e2e8f0',
                      color: '#1e293b',
                      fontWeight: '600'
                    }}
                    onClick={() => handleSelectPlan('starter')}
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" /> : (isInSetupFlow ? 'Select Plan' : 'Start Free Trial')}
                  </Button>
                </CardBody>
              </Card>
            </Col>

            {/* Advanced */}
            <Col xl={3} lg={4} md={6}>
              <Card className="pricing-card h-100 border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                <CardBody className="p-4 d-flex flex-column">
                  <div className="d-flex align-items-center mb-4">
                    <div className="flex-shrink-0">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{
                        width: '48px', height: '48px',
                        background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)'
                      }}>
                        <i className="ri-flight-takeoff-line fs-20" style={{ color: '#3b82f6' }}></i>
                      </div>
                    </div>
                    <div className="ms-3">
                      <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Advanced</h5>
                      <span className="text-muted small">Up to 500 SKUs</span>
                    </div>
                  </div>

                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      <div className="mb-4">
                        <span className="fs-14 text-muted">$</span>
                        <span className="display-5 fw-bold" style={{ color: '#1e293b' }}>29.90</span>
                        <span className="text-muted">/month</span>
                      </div>
                    </TabPane>
                    <TabPane tabId="2">
                      <div className="mb-4">
                        <span className="fs-14 text-muted">$</span>
                        <span className="display-5 fw-bold" style={{ color: '#1e293b' }}>287</span>
                        <span className="text-muted">/year</span>
                        <div><small className="text-success">Save $72/year</small></div>
                      </div>
                    </TabPane>
                  </TabContent>

                  <p className="text-muted small mb-4">For growing e-commerce sellers</p>

                  <ul className="list-unstyled mb-4 flex-grow-1">
                    {FEATURES.advanced.map((feature, idx) => (
                      <li key={idx} className="d-flex align-items-start mb-2">
                        <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                        <span className="text-muted small">
                          {feature.ai && <Badge color="info" className="me-1" pill style={{fontSize: '9px'}}>AI</Badge>}
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="w-100 mt-auto"
                    style={{
                      background: 'transparent',
                      border: '2px solid #e2e8f0',
                      color: '#1e293b',
                      fontWeight: '600'
                    }}
                    onClick={() => handleSelectPlan('advanced')}
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" /> : (isInSetupFlow ? 'Select Plan' : 'Start Free Trial')}
                  </Button>
                </CardBody>
              </Card>
            </Col>

            {/* Professional - Popular */}
            <Col xl={3} lg={4} md={6}>
              <Card className="pricing-card h-100 border-0 shadow-lg position-relative" style={{
                background: 'linear-gradient(180deg, #fff 0%, #fefefe 100%)',
                transform: 'scale(1.05)',
                zIndex: 2,
                borderRadius: '16px'
              }}>
                <div className="position-absolute w-100 text-center" style={{ top: '-14px' }}>
                  <span className="px-4 py-1 rounded-pill text-white fw-semibold" style={{
                    background: 'linear-gradient(135deg, #f97316, #fb923c)',
                    fontSize: '12px'
                  }}>
                    MOST POPULAR
                  </span>
                </div>
                <CardBody className="p-4 d-flex flex-column">
                  <div className="d-flex align-items-center mb-4">
                    <div className="flex-shrink-0">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{
                        width: '48px', height: '48px',
                        background: 'linear-gradient(135deg, #f97316, #fb923c)'
                      }}>
                        <i className="ri-vip-crown-line fs-20 text-white"></i>
                      </div>
                    </div>
                    <div className="ms-3">
                      <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Professional</h5>
                      <span className="text-muted small">Up to 1,000 SKUs</span>
                    </div>
                  </div>

                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      <div className="mb-4">
                        <span className="fs-14 text-muted">$</span>
                        <span className="display-5 fw-bold" style={{ color: '#f97316' }}>39.90</span>
                        <span className="text-muted">/month</span>
                      </div>
                    </TabPane>
                    <TabPane tabId="2">
                      <div className="mb-4">
                        <span className="fs-14 text-muted">$</span>
                        <span className="display-5 fw-bold" style={{ color: '#f97316' }}>383</span>
                        <span className="text-muted">/year</span>
                        <div><small className="text-success">Save $96/year</small></div>
                      </div>
                    </TabPane>
                  </TabContent>

                  <p className="text-muted small mb-4">Best for growing e-commerce sellers</p>

                  <ul className="list-unstyled mb-4 flex-grow-1">
                    {FEATURES.professional.map((feature, idx) => (
                      <li key={idx} className="d-flex align-items-start mb-2">
                        <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                        <span className="text-muted small">
                          {feature.ai && <Badge color="info" className="me-1" pill style={{fontSize: '9px'}}>AI</Badge>}
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="w-100 mt-auto"
                    style={{
                      background: 'linear-gradient(135deg, #f97316, #fb923c)',
                      border: 'none',
                      fontWeight: '600',
                      padding: '12px',
                      color: '#fff'
                    }}
                    onClick={() => handleSelectPlan('professional')}
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" /> : (
                      <>
                        <i className="ri-sparkling-fill me-1"></i>
                        {isInSetupFlow ? 'Select Plan' : 'Start Free Trial'}
                      </>
                    )}
                  </Button>
                </CardBody>
              </Card>
            </Col>

            {/* Business */}
            <Col xl={3} lg={4} md={6}>
              <Card className="pricing-card h-100 border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                <CardBody className="p-4 d-flex flex-column">
                  <div className="d-flex align-items-center mb-4">
                    <div className="flex-shrink-0">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{
                        width: '48px', height: '48px',
                        background: 'linear-gradient(135deg, #fef3c7, #fde68a)'
                      }}>
                        <i className="ri-building-line fs-20" style={{ color: '#f59e0b' }}></i>
                      </div>
                    </div>
                    <div className="ms-3">
                      <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Business</h5>
                      <span className="text-muted small">Up to 2,000 SKUs</span>
                    </div>
                  </div>

                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      <div className="mb-4">
                        <span className="fs-14 text-muted">$</span>
                        <span className="display-5 fw-bold" style={{ color: '#1e293b' }}>69.90</span>
                        <span className="text-muted">/month</span>
                      </div>
                    </TabPane>
                    <TabPane tabId="2">
                      <div className="mb-4">
                        <span className="fs-14 text-muted">$</span>
                        <span className="display-5 fw-bold" style={{ color: '#1e293b' }}>671</span>
                        <span className="text-muted">/year</span>
                        <div><small className="text-success">Save $168/year</small></div>
                      </div>
                    </TabPane>
                  </TabContent>

                  <p className="text-muted small mb-4">For established sellers scaling up</p>

                  <ul className="list-unstyled mb-4 flex-grow-1">
                    {FEATURES.business.map((feature, idx) => (
                      <li key={idx} className="d-flex align-items-start mb-2">
                        <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                        <span className="text-muted small">
                          {feature.ai && <Badge color="info" className="me-1" pill style={{fontSize: '9px'}}>AI</Badge>}
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="w-100 mt-auto"
                    style={{
                      background: 'transparent',
                      border: '2px solid #e2e8f0',
                      color: '#1e293b',
                      fontWeight: '600'
                    }}
                    onClick={() => handleSelectPlan('business')}
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" /> : (isInSetupFlow ? 'Select Plan' : 'Start Free Trial')}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Large Scale Plans - Row 2: Enterprise, Enterprise Plus, Agency, Ultimate */}
          <Row className="g-4 justify-content-center">
            {/* Enterprise, Enterprise Plus, Agency */}
            {LARGE_SCALE_PLANS.map((largePlan) => (
              <Col xl={3} lg={4} md={6} key={largePlan.plan}>
                <Card className={`pricing-card h-100 border-0 shadow-sm`} style={{ borderRadius: '16px', borderLeft: `4px solid ${largePlan.color === 'warning' ? '#f59e0b' : '#3b82f6'}` }}>
                  <CardBody className="p-4 d-flex flex-column">
                    <div className="d-flex align-items-center mb-4">
                      <div className="flex-shrink-0">
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{
                          width: '48px', height: '48px',
                          background: largePlan.color === 'warning'
                            ? 'linear-gradient(135deg, #fef3c7, #fde68a)'
                            : 'linear-gradient(135deg, #dbeafe, #bfdbfe)'
                        }}>
                          <i className={`${largePlan.icon} fs-20`} style={{ color: largePlan.color === 'warning' ? '#f59e0b' : '#3b82f6' }}></i>
                        </div>
                      </div>
                      <div className="ms-3">
                        <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>{largePlan.name}</h5>
                        <span className="text-muted small">Up to {largePlan.products.toLocaleString()} SKUs</span>
                      </div>
                    </div>

                    <TabContent activeTab={activeTab}>
                      <TabPane tabId="1">
                        <div className="mb-4">
                          <span className="fs-14 text-muted">$</span>
                          <span className="display-5 fw-bold" style={{ color: '#1e293b' }}>{largePlan.monthlyPrice}</span>
                          <span className="text-muted">/month</span>
                        </div>
                      </TabPane>
                      <TabPane tabId="2">
                        <div className="mb-4">
                          <span className="fs-14 text-muted">$</span>
                          <span className="display-5 fw-bold" style={{ color: '#1e293b' }}>{largePlan.yearlyPrice}</span>
                          <span className="text-muted">/year</span>
                          <div><small className="text-success">Save ${Math.round(largePlan.monthlyPrice * 12 - largePlan.yearlyPrice)}/year</small></div>
                        </div>
                      </TabPane>
                    </TabContent>

                    <p className="text-muted small mb-4">For high-volume sellers</p>

                    <ul className="list-unstyled mb-4 flex-grow-1">
                      <li className="d-flex align-items-start mb-2">
                        <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                        <span className="text-muted small">{largePlan.products.toLocaleString()} managed SKUs</span>
                      </li>
                      <li className="d-flex align-items-start mb-2">
                        <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                        <span className="text-muted small">
                          <Badge color="info" className="me-1" pill style={{fontSize: '9px'}}>AI</Badge>
                          AI-Powered Category Matching
                        </span>
                      </li>
                      <li className="d-flex align-items-start mb-2">
                        <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                        <span className="text-muted small">
                          <Badge color="info" className="me-1" pill style={{fontSize: '9px'}}>AI</Badge>
                          AI Title, Description & Repricing
                        </span>
                      </li>
                      <li className="d-flex align-items-start mb-2">
                        <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                        <span className="text-muted small">Price & Stock Sync ({largePlan.syncInterval})</span>
                      </li>
                      <li className="d-flex align-items-start mb-2">
                        <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                        <span className="text-muted small">{largePlan.ebayAccounts} eBay Accounts</span>
                      </li>
                      <li className="d-flex align-items-start mb-2">
                        <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                        <span className="text-muted small">All HTML Templates</span>
                      </li>
                    </ul>

                    <Button
                      className="w-100 mt-auto"
                      style={{
                        background: 'transparent',
                        border: `2px solid ${largePlan.color === 'warning' ? '#f59e0b' : '#3b82f6'}`,
                        color: largePlan.color === 'warning' ? '#f59e0b' : '#3b82f6',
                        fontWeight: '600'
                      }}
                      onClick={() => handleSelectPlan(largePlan.plan)}
                      disabled={loading}
                    >
                      {loading ? <Spinner size="sm" /> : (isInSetupFlow ? 'Select Plan' : 'Start Free Trial')}
                    </Button>
                  </CardBody>
                </Card>
              </Col>
            ))}

            {/* Ultimate Card with Dropdown (50K - 100K) */}
            <Col xl={3} lg={4} md={6}>
              <Card className="pricing-card h-100 border-0 shadow-sm position-relative" style={{ borderRadius: '16px', borderLeft: '4px solid #7c3aed' }}>
                <div className="position-absolute" style={{ top: '12px', right: '12px' }}>
                  <Badge className="px-2" style={{ background: '#7c3aed' }}>Scale</Badge>
                </div>
                <CardBody className="p-4 d-flex flex-column">
                  <div className="d-flex align-items-center mb-4">
                    <div className="flex-shrink-0">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{
                        width: '48px', height: '48px',
                        background: 'linear-gradient(135deg, #ddd6fe, #c4b5fd)'
                      }}>
                        <i className="ri-vip-diamond-line fs-20" style={{ color: '#7c3aed' }}></i>
                      </div>
                    </div>
                    <div className="ms-3">
                      <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Ultimate</h5>
                      <span className="text-muted small">50,000 - 100,000 SKUs</span>
                    </div>
                  </div>

                  {/* SKU Tier Dropdown */}
                  <div className="mb-3">
                    <Dropdown isOpen={ultimateDropdownOpen} toggle={() => setUltimateDropdownOpen(!ultimateDropdownOpen)} direction="down">
                      <DropdownToggle caret className="w-100 d-flex justify-content-between align-items-center" style={{
                        background: 'rgba(124, 58, 237, 0.1)',
                        border: '1px solid rgba(124, 58, 237, 0.2)',
                        color: '#7c3aed'
                      }}>
                        <span className="text-nowrap">{ULTIMATE_TIERS[selectedUltimateTier]?.label || ULTIMATE_TIERS[0].label}</span>
                      </DropdownToggle>
                      <DropdownMenu className="w-100">
                        {ULTIMATE_TIERS.map((tier, idx) => (
                          <DropdownItem
                            key={tier.plan}
                            active={selectedUltimateTier === idx}
                            onClick={() => setSelectedUltimateTier(idx)}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span>{tier.label}</span>
                              <Badge color="light" className="text-dark">
                                ${activeTab === '2' ? tier.yearlyPrice : tier.monthlyPrice}
                              </Badge>
                            </div>
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>

                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      <div className="mb-4">
                        <span className="fs-14 text-muted">$</span>
                        <span className="display-5 fw-bold" style={{ color: '#7c3aed' }}>
                          {ULTIMATE_TIERS[selectedUltimateTier]?.monthlyPrice || ULTIMATE_TIERS[0].monthlyPrice}
                        </span>
                        <span className="text-muted">/month</span>
                      </div>
                    </TabPane>
                    <TabPane tabId="2">
                      <div className="mb-4">
                        <span className="fs-14 text-muted">$</span>
                        <span className="display-5 fw-bold" style={{ color: '#7c3aed' }}>
                          {ULTIMATE_TIERS[selectedUltimateTier]?.yearlyPrice || ULTIMATE_TIERS[0].yearlyPrice}
                        </span>
                        <span className="text-muted">/year</span>
                        <div>
                          <small className="text-success">
                            Save ${Math.round((ULTIMATE_TIERS[selectedUltimateTier]?.monthlyPrice || ULTIMATE_TIERS[0].monthlyPrice) * 12 - (ULTIMATE_TIERS[selectedUltimateTier]?.yearlyPrice || ULTIMATE_TIERS[0].yearlyPrice))}/year
                          </small>
                        </div>
                      </div>
                    </TabPane>
                  </TabContent>

                  <p className="text-muted small mb-4">For enterprise-level operations</p>

                  <ul className="list-unstyled mb-4 flex-grow-1">
                    <li className="d-flex align-items-start mb-2">
                      <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                      <span className="text-muted small">Up to {(ULTIMATE_TIERS[selectedUltimateTier]?.products || ULTIMATE_TIERS[0].products).toLocaleString()} managed SKUs</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                      <span className="text-muted small">
                        <Badge color="info" className="me-1" pill style={{fontSize: '9px'}}>AI</Badge>
                        AI-Powered Category Matching
                      </span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                      <span className="text-muted small">
                        <Badge color="info" className="me-1" pill style={{fontSize: '9px'}}>AI</Badge>
                        AI Title, Description & Repricing
                      </span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                      <span className="text-muted small">Real-time Price & Stock Sync</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                      <span className="text-muted small">10 eBay Accounts</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="ri-check-line me-2 mt-1" style={{ color: '#22c55e' }}></i>
                      <span className="text-muted small">All HTML Templates</span>
                    </li>
                  </ul>

                  <Button
                    className="w-100 mt-auto"
                    style={{
                      background: '#7c3aed',
                      border: 'none',
                      fontWeight: '600',
                      color: '#fff'
                    }}
                    onClick={() => handleSelectPlan(ULTIMATE_TIERS[selectedUltimateTier]?.plan || ULTIMATE_TIERS[0].plan)}
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" /> : (isInSetupFlow ? 'Select Plan' : 'Start Free Trial')}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Feature Comparison */}
          <Row className="justify-content-center mt-5 pt-4">
            <Col lg={12}>
              <div className="text-center mb-5">
                <h2 className="fw-bold mb-2" style={{ color: '#1e293b' }}>Compare All Features</h2>
                <p className="text-muted">See what's included in each plan</p>
              </div>

              <Card className="border-0 shadow-sm overflow-hidden" style={{ borderRadius: '16px' }}>
                <CardBody className="p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead style={{ background: '#f8fafc' }}>
                        <tr>
                          <th scope="col" className="py-3 ps-4" style={{ width: '20%', color: '#64748b' }}>Features</th>
                          <th scope="col" className="text-center py-3" style={{ color: '#64748b' }}>Starter</th>
                          <th scope="col" className="text-center py-3" style={{ color: '#64748b' }}>Advanced</th>
                          <th scope="col" className="text-center py-3" style={{ background: '#fff7ed', color: '#f97316' }}>Professional</th>
                          <th scope="col" className="text-center py-3" style={{ color: '#64748b' }}>Business</th>
                          <th scope="col" className="text-center py-3" style={{ color: '#64748b' }}>Enterprise+</th>
                          <th scope="col" className="text-center py-3" style={{ color: '#7c3aed' }}>Ultimate</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="fw-medium ps-4 py-3">SKU Limit</td>
                          <td className="text-center py-3">200</td>
                          <td className="text-center py-3">500</td>
                          <td className="text-center py-3 fw-semibold" style={{ background: '#fffbeb' }}>1,000</td>
                          <td className="text-center py-3">2,000</td>
                          <td className="text-center py-3">5K - 25K</td>
                          <td className="text-center py-3">50K - 100K</td>
                        </tr>
                        <tr>
                          <td className="fw-medium ps-4 py-3">Monthly Price</td>
                          <td className="text-center py-3">$14.90</td>
                          <td className="text-center py-3">$29.90</td>
                          <td className="text-center py-3 fw-semibold" style={{ background: '#fffbeb' }}>$39.90</td>
                          <td className="text-center py-3">$69.90</td>
                          <td className="text-center py-3">$99.90+</td>
                          <td className="text-center py-3">$399.90+</td>
                        </tr>
                        <tr>
                          <td className="fw-medium ps-4 py-3">
                            <Badge color="info" pill className="me-1" style={{fontSize: '9px'}}>AI</Badge>
                            Category Matching
                          </td>
                          <td className="text-center py-3"><i className="ri-check-line fs-18" style={{ color: '#22c55e' }}></i></td>
                          <td className="text-center py-3"><i className="ri-check-line fs-18" style={{ color: '#22c55e' }}></i></td>
                          <td className="text-center py-3" style={{ background: '#fffbeb' }}><i className="ri-check-line fs-18" style={{ color: '#22c55e' }}></i></td>
                          <td className="text-center py-3"><i className="ri-check-line fs-18" style={{ color: '#22c55e' }}></i></td>
                          <td className="text-center py-3"><i className="ri-check-line fs-18" style={{ color: '#22c55e' }}></i></td>
                          <td className="text-center py-3"><i className="ri-check-line fs-18" style={{ color: '#22c55e' }}></i></td>
                        </tr>
                        <tr>
                          <td className="fw-medium ps-4 py-3">
                            <Badge color="info" pill className="me-1" style={{fontSize: '9px'}}>AI</Badge>
                            Smart Repricing
                          </td>
                          <td className="text-center py-3"><i className="ri-close-line fs-18 text-muted"></i></td>
                          <td className="text-center py-3"><i className="ri-close-line fs-18 text-muted"></i></td>
                          <td className="text-center py-3" style={{ background: '#fffbeb' }}><i className="ri-check-line fs-18" style={{ color: '#22c55e' }}></i></td>
                          <td className="text-center py-3"><i className="ri-check-line fs-18" style={{ color: '#22c55e' }}></i></td>
                          <td className="text-center py-3"><i className="ri-check-line fs-18" style={{ color: '#22c55e' }}></i></td>
                          <td className="text-center py-3"><i className="ri-check-line fs-18" style={{ color: '#22c55e' }}></i></td>
                        </tr>
                        <tr>
                          <td className="fw-medium ps-4 py-3">Price & Stock Sync</td>
                          <td className="text-center py-3">2 hours</td>
                          <td className="text-center py-3">1 hour</td>
                          <td className="text-center py-3 fw-semibold" style={{ background: '#fffbeb' }}>30 min</td>
                          <td className="text-center py-3">15 min</td>
                          <td className="text-center py-3">5 - 10 min</td>
                          <td className="text-center py-3">Real-time</td>
                        </tr>
                        <tr>
                          <td className="fw-medium ps-4 py-3">eBay Accounts</td>
                          <td className="text-center py-3">1</td>
                          <td className="text-center py-3">2</td>
                          <td className="text-center py-3 fw-semibold" style={{ background: '#fffbeb' }}>2</td>
                          <td className="text-center py-3">3</td>
                          <td className="text-center py-3">3 - 5</td>
                          <td className="text-center py-3">10</td>
                        </tr>
                        <tr>
                          <td className="fw-medium ps-4 py-3">HTML Templates</td>
                          <td className="text-center py-3">3</td>
                          <td className="text-center py-3">5</td>
                          <td className="text-center py-3 fw-semibold" style={{ background: '#fffbeb' }}>7</td>
                          <td className="text-center py-3">All</td>
                          <td className="text-center py-3">All</td>
                          <td className="text-center py-3">All</td>
                        </tr>
                        <tr>
                          <td className="fw-medium ps-4 py-3">Support</td>
                          <td className="text-center py-3">Email</td>
                          <td className="text-center py-3">Email</td>
                          <td className="text-center py-3 fw-semibold" style={{ background: '#fffbeb' }}>Priority</td>
                          <td className="text-center py-3">Priority</td>
                          <td className="text-center py-3">Priority</td>
                          <td className="text-center py-3">24/7</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* FAQ Section */}
          <Row className="justify-content-center mt-5 pt-4">
            <Col lg={8}>
              <div className="text-center mb-5">
                <h2 className="fw-bold mb-2" style={{ color: '#1e293b' }}>Frequently Asked Questions</h2>
                <p className="text-muted">Got questions? We've got answers.</p>
              </div>

              <div className="vstack gap-3">
                {[
                  {
                    q: "Can I try CodeRom for free?",
                    a: "Yes! All plans come with a 14-day free trial. No credit card required to start. You can test all features before committing to a paid plan."
                  },
                  {
                    q: "What happens if I exceed my SKU limit?",
                    a: "We'll notify you when you're approaching your limit. You can easily upgrade to a higher plan at any time. Your existing listings won't be affected."
                  },
                  {
                    q: "How does the AI category matching work?",
                    a: "Our AI analyzes product titles, descriptions, and attributes to automatically match Amazon categories to the correct eBay categories with 98% accuracy."
                  },
                  {
                    q: "Can I cancel my subscription anytime?",
                    a: "Yes, you can cancel your subscription at any time. There are no long-term contracts or cancellation fees."
                  }
                ].map((faq, idx) => (
                  <Card key={idx} className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    <CardBody className="p-4">
                      <h6 className="mb-2 fw-semibold" style={{ color: '#1e293b' }}>
                        <i className="ri-question-line me-2" style={{ color: '#f97316' }}></i>
                        {faq.q}
                      </h6>
                      <p className="text-muted mb-0 ms-4">{faq.a}</p>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </Col>
          </Row>

          {/* CTA Section */}
          <Row className="justify-content-center mt-5 pt-4 pb-5">
            <Col lg={10}>
              <Card className="border-0 overflow-hidden" style={{
                background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                borderRadius: '24px'
              }}>
                <CardBody className="p-5 text-center">
                  <h2 className="text-white fw-bold mb-3">Ready to Scale Your Business?</h2>
                  <p className="text-white-50 mb-4 lead">
                    Join thousands of sellers who trust CodeRom to automate their e-commerce operations.
                  </p>
                  <Button
                    size="lg"
                    className="px-5"
                    style={{
                      background: '#fff',
                      color: '#f97316',
                      fontWeight: '600',
                      borderRadius: '50px',
                      border: 'none'
                    }}
                    onClick={() => navigate('/register')}
                  >
                    <i className="ri-rocket-line me-2"></i>
                    Start Your Free Trial
                  </Button>
                  <p className="text-white-50 small mt-3 mb-0">
                    No credit card required • 14-day free trial • Cancel anytime
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>

        </Container>
      </div>

      {/* Footer */}
      <Footer />

      {/* Custom CSS */}
      <style>{`
        .pricing-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .pricing-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1) !important;
        }
        .plan-nav .nav-link {
          transition: all 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default PricingPage;
